import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { POSTS } from "@/data/blog";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function JournalTeaser() {
  const featured = POSTS.slice(0, 2);
  if (featured.length === 0) return null;
  const [primary, ...rest] = featured;

  return (
    <section
      id="journal"
      data-testid="journal-teaser-section"
      className="relative bg-[#F9F8F6] py-24 sm:py-32 border-t border-[#E5E1DB]"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8 mb-14 sm:mb-20">
          <div className="reveal max-w-2xl">
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#5C6658] mb-6">
              08 / The Journal
            </p>
            <h2 className="font-[Outfit] font-extralight text-[#2D332B] text-[36px] sm:text-[52px] leading-[1.05] tracking-[-0.015em]">
              Reading room —
              <br />
              <span className="italic text-[#8A9A86]">
                notes from the practice.
              </span>
            </h2>
          </div>
          <Link
            to="/journal"
            data-testid="journal-view-all"
            className="reveal inline-flex items-center gap-3 self-start sm:self-end px-5 py-3 border border-[#2D332B] text-[#2D332B] text-[11px] tracking-[0.25em] uppercase hover:bg-[#2D332B] hover:text-[#F9F8F6] transition-colors duration-500 rounded-sm"
          >
            View all essays <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10">
          {/* Featured */}
          <Link
            to={`/journal/${primary.slug}`}
            data-testid={`journal-teaser-${primary.slug}`}
            className="reveal lg:col-span-7 group block border border-[#E5E1DB] rounded-sm overflow-hidden hover:border-[#8A9A86] transition-colors duration-500"
          >
            <div className="relative aspect-[16/10] overflow-hidden">
              <img
                src={primary.cover}
                alt={primary.title}
                className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.04]"
              />
            </div>
            <div className="p-8 sm:p-10">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
                {formatDate(primary.published_at)} · {primary.reading_minutes} min read
              </p>
              <h3 className="mt-4 font-[Outfit] font-light text-[28px] sm:text-[36px] text-[#2D332B] leading-[1.12]">
                {primary.title}
              </h3>
              {primary.subtitle && (
                <p className="mt-3 italic text-[16px] text-[#5C6658]">
                  {primary.subtitle}
                </p>
              )}
              <p className="mt-5 text-[15px] leading-relaxed text-[#5C6658] line-clamp-3">
                {primary.excerpt}
              </p>
              <span className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#2D332B] group-hover:text-[#8A9A86]">
                Read essay <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>

          {/* Sidebar posts / placeholder */}
          <div className="reveal lg:col-span-5 space-y-8">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/journal/${p.slug}`}
                className="group block border border-[#E5E1DB] rounded-sm overflow-hidden hover:border-[#8A9A86] transition-colors"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms]"
                  />
                </div>
                <div className="p-6">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
                    {formatDate(p.published_at)}
                  </p>
                  <h4 className="mt-3 font-[Outfit] font-light text-[20px] text-[#2D332B] leading-[1.2]">
                    {p.title}
                  </h4>
                </div>
              </Link>
            ))}

            {rest.length === 0 && (
              <div className="border border-dashed border-[#E5E1DB] rounded-sm p-8 sm:p-10 bg-[#F2EFEB]">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
                  More coming
                </p>
                <p className="mt-4 font-[Outfit] font-light text-[24px] text-[#2D332B] leading-[1.2]">
                  A slow, considered journal on plants, spaces and craft.
                </p>
                <p className="mt-4 text-[14px] leading-relaxed text-[#5C6658]">
                  New essays are published from the studio a few times a season.
                  Reach out if there&rsquo;s a subject you&rsquo;d like us to
                  write about.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
